import axios from "axios";
import {EndPoints} from "./EndPoints";
import {AddUserDTO, EditUserDTO, EditUserPermissionsDTO, PasswordResetDTO} from "./dtos";
import {getAppToken} from "../shared/utils";
import {setLoadUsersTaskStatus} from "../features/settings/settingsSlice";
import {AsyncTaskStatusType, ResponseDTO} from "../shared/dtos";
import {
    setAllPaginatedUsers,
    setAllUsersLookup,
    setCreateUserTaskStatus,
    setEditUserPermissionsTaskStatus,
    setEditUserTaskStatus,
    setGetAllUsersLookupTaskStatus,
    setGetLogsTaskStatus,
    setGetMailLogsTaskStatus,
    setGetNonCompanyUsersLookupTaskStatus,
    setLogs,
    setMailLogs,
    setNonCompanyUsersLookup,
    setResetOwnPasswordTaskStatus,
    setResetPasswordTaskStatus
} from "../features/app-user/appUserSlice";
import {AppState} from "../store";
import {createAsyncThunk} from "@reduxjs/toolkit";
import {ErrorMessages, Headers} from "./constants";

export const createUserAction = createAsyncThunk(
    'user/createUser',
    async (dto: AddUserDTO, {dispatch, getState, rejectWithValue, fulfillWithValue}: any) => {
        dispatch(setCreateUserTaskStatus({type: AsyncTaskStatusType.Loading}));

        try {
            const token = await getAppToken(dispatch, getState);
            if (!token) {
                throw new Error(ErrorMessages.TOKEN_MISSING);
            }

            const response = await axios.post(`${EndPoints.user}/`, dto, {
                headers: {
                    [Headers.AUTHORIZATION]: `Bearer ${token}`,
                    [Headers.CONTENT_TYPE]: Headers.APPLICATION_JSON,
                    [Headers.ACCEPT]: Headers.APPLICATION_JSON,
                }
            });

            const {code, message, errorDetails, fieldErrors} = response.data;
            if (code === ResponseDTO.CodeEnum.Success) {
                dispatch(setCreateUserTaskStatus({type: AsyncTaskStatusType.Success}));
                return fulfillWithValue(true);
            } else if (code === ResponseDTO.CodeEnum.DuplicateObject) {
                dispatch(setCreateUserTaskStatus({
                    type: AsyncTaskStatusType.Error,
                    errorMessage: 'Bu kullanıcı mevcut'
                }));
                return rejectWithValue('Bu kullanıcı mevcut');
            } else {
                dispatch(setCreateUserTaskStatus({
                    type: AsyncTaskStatusType.Error,
                    errorMessage: message || ErrorMessages.GENERIC_ERROR,
                    errorDetails,
                    fieldErrors
                }));
                return rejectWithValue(message || ErrorMessages.GENERIC_ERROR);
            }
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || ErrorMessages.UNKNOWN_ERROR;
            dispatch(setCreateUserTaskStatus({
                type: AsyncTaskStatusType.Error,
                errorMessage
            }));
            return rejectWithValue(errorMessage);
        }
    }
);

export const editUserAction = createAsyncThunk(
    'user/editUser',
    async ({id, dto}: { id: number, dto: EditUserDTO }, {
        dispatch,
        getState,
        rejectWithValue,
        fulfillWithValue
    }: any) => {
        dispatch(setEditUserTaskStatus({type: AsyncTaskStatusType.Loading}));

        try {
            const token = await getAppToken(dispatch, getState);
            if (!token) {
                throw new Error(ErrorMessages.TOKEN_MISSING);
            }

            const response = await axios.put(`${EndPoints.user}/${id}`, dto, {
                headers: {
                    [Headers.AUTHORIZATION]: `Bearer ${token}`,
                    [Headers.CONTENT_TYPE]: Headers.APPLICATION_JSON,
                    [Headers.ACCEPT]: Headers.APPLICATION_JSON,
                }
            });

            const {code, message, errorDetails, fieldErrors} = response.data;
            if (code === ResponseDTO.CodeEnum.Success) {
                dispatch(setEditUserTaskStatus({type: AsyncTaskStatusType.Success}));
                return fulfillWithValue(true);
            } else {
                dispatch(setEditUserTaskStatus({
                    type: AsyncTaskStatusType.Error,
                    errorMessage: message || ErrorMessages.GENERIC_ERROR,
                    errorDetails,
                    fieldErrors
                }));
                return rejectWithValue(message || ErrorMessages.GENERIC_ERROR);
            }
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || ErrorMessages.UNKNOWN_ERROR;
            dispatch(setEditUserTaskStatus({
                type: AsyncTaskStatusType.Error,
                errorMessage
            }));
            return rejectWithValue(errorMessage);
        }
    }
);

export const editUserPermissionsAction = createAsyncThunk(
    'user/editUserPermissions',
    async ({id, dto}: { id: number; dto: EditUserPermissionsDTO }, {
        dispatch,
        getState,
        rejectWithValue,
        fulfillWithValue
    }: any) => {
        dispatch(setEditUserPermissionsTaskStatus({type: AsyncTaskStatusType.Loading}));

        try {
            const token = await getAppToken(dispatch, getState);
            if (!token) {
                throw new Error(ErrorMessages.TOKEN_MISSING);
            }

            const response = await axios.put(`${EndPoints.user}/${id}/edit-permissions`, dto, {
                headers: {
                    [Headers.AUTHORIZATION]: `Bearer ${token}`,
                    [Headers.CONTENT_TYPE]: Headers.APPLICATION_JSON,
                    [Headers.ACCEPT]: Headers.APPLICATION_JSON,
                },
            });

            const {code, message, errorDetails, fieldErrors} = response.data;

            if (code === ResponseDTO.CodeEnum.Success) {
                dispatch(setEditUserPermissionsTaskStatus({type: AsyncTaskStatusType.Success}));
                return fulfillWithValue(true);
            } else {
                dispatch(setEditUserPermissionsTaskStatus({
                    type: AsyncTaskStatusType.Error,
                    errorMessage: message || ErrorMessages.GENERIC_ERROR,
                    errorDetails,
                    fieldErrors,
                }));
                return rejectWithValue(message || ErrorMessages.GENERIC_ERROR);
            }
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || ErrorMessages.UNKNOWN_ERROR;
            dispatch(setEditUserPermissionsTaskStatus({
                type: AsyncTaskStatusType.Error,
                errorMessage,
            }));
            return rejectWithValue(errorMessage);
        }
    }
);

export const getUsersAction = createAsyncThunk(
    'user/getUsers',
    async ({emailSearchText, nameSearchText, roleFilter, companyIdFilter, page = 1}: {
        emailSearchText?: string, nameSearchText?: string, roleFilter?: string, companyIdFilter?: number, page: number
    }, {dispatch, getState, rejectWithValue, fulfillWithValue}) => {
        dispatch(setLoadUsersTaskStatus({type: AsyncTaskStatusType.Loading}));

        try {
            const token = await getAppToken(dispatch, getState as () => AppState);
            if (!token) {
                throw new Error(ErrorMessages.TOKEN_MISSING);
            }

            const params = new URLSearchParams({page: page.toString()});
            if (emailSearchText) params.append("emailSearchText", emailSearchText);
            if (nameSearchText) params.append("nameSearchText", nameSearchText);
            if (roleFilter) params.append("roleFilter", roleFilter);
            if (companyIdFilter) params.append("companyIdFilter", companyIdFilter.toString());

            const response = await axios.get(`${EndPoints.users}?${params.toString()}`, {
                headers: {
                    [Headers.AUTHORIZATION]: `Bearer ${token}`,
                    [Headers.CONTENT_TYPE]: Headers.APPLICATION_JSON,
                    [Headers.ACCEPT]: Headers.APPLICATION_JSON,
                },
            });

            const {code, message, errorDetails, resultValue} = response.data;
            if (code === ResponseDTO.CodeEnum.Success) {
                dispatch(setLoadUsersTaskStatus({type: AsyncTaskStatusType.Success}));
                dispatch(setAllPaginatedUsers(resultValue));
                return fulfillWithValue(resultValue);
            } else {
                dispatch(setLoadUsersTaskStatus({
                    type: AsyncTaskStatusType.Error,
                    errorMessage: message,
                    errorDetails,
                }));
                return rejectWithValue(message);
            }
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || ErrorMessages.UNKNOWN_ERROR;
            dispatch(setLoadUsersTaskStatus({
                type: AsyncTaskStatusType.Error,
                errorMessage
            }));
            return rejectWithValue(errorMessage);
        }
    }
);

export const getAllUsersAction = createAsyncThunk(
    'user/getAllUsers',
    async (_, {dispatch, getState, rejectWithValue, fulfillWithValue}) => {
        dispatch(setGetAllUsersLookupTaskStatus({type: AsyncTaskStatusType.Loading}));

        try {
            const token = await getAppToken(dispatch, getState as () => AppState);
            if (!token) {
                throw new Error(ErrorMessages.TOKEN_MISSING);
            }

            const response = await axios.get(EndPoints.user + "/allUsers", {
                headers: {
                    [Headers.AUTHORIZATION]: `Bearer ${token}`,
                    [Headers.CONTENT_TYPE]: Headers.APPLICATION_JSON,
                    [Headers.ACCEPT]: Headers.APPLICATION_JSON,
                }
            });

            const {code, message, errorDetails, resultValue} = response.data;

            if (code === ResponseDTO.CodeEnum.Success) {
                dispatch(setGetAllUsersLookupTaskStatus({type: AsyncTaskStatusType.Success}));
                dispatch(setAllUsersLookup(resultValue));
                return fulfillWithValue(resultValue);
            } else {
                dispatch(setGetAllUsersLookupTaskStatus({
                    type: AsyncTaskStatusType.Error,
                    errorMessage: message,
                    errorDetails,
                }));
                return rejectWithValue(message);
            }
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || ErrorMessages.UNKNOWN_ERROR;
            dispatch(setGetAllUsersLookupTaskStatus({
                type: AsyncTaskStatusType.Error,
                errorMessage
            }));
            return rejectWithValue(errorMessage);
        }
    }
);

export const getNonCompanyUsersAction = createAsyncThunk(
    'user/non-company-users',
    async (_, {dispatch, getState, rejectWithValue, fulfillWithValue}) => {
        dispatch(setGetNonCompanyUsersLookupTaskStatus({type: AsyncTaskStatusType.Loading}));

        try {
            const token = await getAppToken(dispatch, getState as () => AppState);
            if (!token) {
                throw new Error(ErrorMessages.TOKEN_MISSING);
            }

            const response = await axios.get(EndPoints.user + "/non-company-users", {
                headers: {
                    [Headers.AUTHORIZATION]: `Bearer ${token}`,
                    [Headers.CONTENT_TYPE]: Headers.APPLICATION_JSON,
                    [Headers.ACCEPT]: Headers.APPLICATION_JSON,
                }
            });

            const {code, message, errorDetails, resultValue} = response.data;

            if (code === ResponseDTO.CodeEnum.Success) {
                dispatch(setGetNonCompanyUsersLookupTaskStatus({type: AsyncTaskStatusType.Success}));
                dispatch(setNonCompanyUsersLookup(resultValue));
                return fulfillWithValue(resultValue);
            } else {
                dispatch(setGetNonCompanyUsersLookupTaskStatus({
                    type: AsyncTaskStatusType.Error,
                    errorMessage: message,
                    errorDetails,
                }));
                return rejectWithValue(message);
            }
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || ErrorMessages.UNKNOWN_ERROR;
            dispatch(setGetNonCompanyUsersLookupTaskStatus({
                type: AsyncTaskStatusType.Error,
                errorMessage
            }));
            return rejectWithValue(errorMessage);
        }
    }
);

export const resetPasswordAction = createAsyncThunk(
    'user/resetPassword',
    async ({id, dto}: { id: number, dto: PasswordResetDTO }, {
        dispatch,
        getState,
        rejectWithValue,
        fulfillWithValue
    }: any) => {
        dispatch(setResetPasswordTaskStatus({type: AsyncTaskStatusType.Loading}));

        try {
            const token = await getAppToken(dispatch, getState);
            if (!token) {
                throw new Error(ErrorMessages.TOKEN_MISSING);
            }

            const response = await axios.put(`${EndPoints.user}/${id}/reset-password`, dto, {
                headers: {
                    [Headers.AUTHORIZATION]: `Bearer ${token}`,
                    [Headers.CONTENT_TYPE]: Headers.APPLICATION_JSON,
                    [Headers.ACCEPT]: Headers.APPLICATION_JSON,
                }
            });

            const {code, message, errorDetails} = response.data;
            if (code === ResponseDTO.CodeEnum.Success) {
                dispatch(setResetPasswordTaskStatus({type: AsyncTaskStatusType.Success}));
                return fulfillWithValue(true);
            } else {
                dispatch(setResetPasswordTaskStatus({
                    type: AsyncTaskStatusType.Error,
                    errorMessage: message || ErrorMessages.GENERIC_ERROR,
                    errorDetails,
                }));
                return rejectWithValue(message || ErrorMessages.GENERIC_ERROR);
            }
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || ErrorMessages.UNKNOWN_ERROR;
            dispatch(setResetPasswordTaskStatus({
                type: AsyncTaskStatusType.Error,
                errorMessage
            }));
            return rejectWithValue(errorMessage);
        }
    }
);

export const resetOwnPasswordAction = createAsyncThunk(
    'user/resetOwnPassword',
    async (dto: PasswordResetDTO, {
        dispatch,
        getState,
        rejectWithValue,
        fulfillWithValue
    }: any) => {
        dispatch(setResetOwnPasswordTaskStatus({type: AsyncTaskStatusType.Loading}));

        try {
            const token = await getAppToken(dispatch, getState);
            if (!token) {
                throw new Error(ErrorMessages.TOKEN_MISSING);
            }

            const response = await axios.put(`${EndPoints.user}/reset-password`, dto, {
                headers: {
                    [Headers.AUTHORIZATION]: `Bearer ${token}`,
                    [Headers.CONTENT_TYPE]: Headers.APPLICATION_JSON,
                    [Headers.ACCEPT]: Headers.APPLICATION_JSON,
                }
            });

            const {code, message, errorDetails, fieldErrors} = response.data;
            if (code === ResponseDTO.CodeEnum.Success) {
                dispatch(setResetOwnPasswordTaskStatus({type: AsyncTaskStatusType.Success}));
                return fulfillWithValue(true);
            } else {
                dispatch(setResetOwnPasswordTaskStatus({
                    type: AsyncTaskStatusType.Error,
                    errorMessage: message || ErrorMessages.GENERIC_ERROR,
                    errorDetails,
                    fieldErrors
                }));
                return rejectWithValue(message || ErrorMessages.GENERIC_ERROR);
            }
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || ErrorMessages.UNKNOWN_ERROR;
            dispatch(setResetOwnPasswordTaskStatus({
                type: AsyncTaskStatusType.Error,
                errorMessage
            }));
            return rejectWithValue(errorMessage);
        }
    }
);

export const activate2FAAction = createAsyncThunk(
    'user/activate2FA',
    async (code: string, {dispatch, getState, rejectWithValue, fulfillWithValue}: any) => {
        dispatch(setLoadUsersTaskStatus({type: AsyncTaskStatusType.Loading}));

        try {
            const token = getState().auth.userWithToken?.token;
            if (!token) {
                throw new Error(ErrorMessages.TOKEN_MISSING);
            }

            const params = new URLSearchParams({code});
            const response = await axios.put(`${EndPoints.user}/mfa/enable?${params.toString()}`, {}, {
                headers: {
                    [Headers.AUTHORIZATION]: `Bearer ${token}`,
                    [Headers.CONTENT_TYPE]: Headers.APPLICATION_JSON,
                    [Headers.ACCEPT]: Headers.APPLICATION_JSON,
                },
            });

            const {message, code: responseCode} = response.data;
            if (responseCode === ResponseDTO.CodeEnum.Success) {
                dispatch(setLoadUsersTaskStatus({type: AsyncTaskStatusType.Success}));
                return fulfillWithValue(true);
            } else {
                dispatch(setLoadUsersTaskStatus({
                    type: AsyncTaskStatusType.Error,
                    errorMessage: message
                }));
                return rejectWithValue(message);
            }
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || ErrorMessages.UNKNOWN_ERROR;
            dispatch(setLoadUsersTaskStatus({
                type: AsyncTaskStatusType.Error,
                errorMessage,
            }));
            return rejectWithValue(errorMessage);
        }
    }
);

export const getLogsAction = createAsyncThunk(
    'logs/getLogs',
    async (
        {
            type,
            level,
            userAction,
            systemAction,
            fromDate,
            toDate,
            userId,
            page = 1,
        }: {
            type: string | null;
            level: string | null;
            userAction: string | null;
            systemAction: string | null;
            fromDate: string | null;
            toDate: string | null;
            userId: number | null;
            page: number;
        },
        {dispatch, getState, rejectWithValue, fulfillWithValue}
    ) => {
        dispatch(setGetLogsTaskStatus({type: AsyncTaskStatusType.Loading}));

        try {
            const token = await getAppToken(dispatch, getState as () => AppState);
            if (!token) {
                throw new Error(ErrorMessages.TOKEN_MISSING);
            }

            const params = new URLSearchParams({page: page.toString()});
            if (type) params.append('type', type);
            if (level) params.append('level', level);
            if (userAction) params.append('userAction', userAction);
            if (systemAction) params.append('systemAction', systemAction);
            if (fromDate) params.append('fromDate', fromDate.toString());
            if (toDate) params.append('toDate', toDate.toString());
            if (userId) params.append('userId', userId.toString());

            const response = await axios.get(`${EndPoints.logs}/user/logs?${params.toString()}`, {
                headers: {
                    [Headers.AUTHORIZATION]: `Bearer ${token}`,
                    [Headers.CONTENT_TYPE]: Headers.APPLICATION_JSON,
                    [Headers.ACCEPT]: Headers.APPLICATION_JSON,
                },
            });

            const {code, message, errorDetails, resultValue} = response.data;
            if (code === ResponseDTO.CodeEnum.Success) {
                dispatch(setGetLogsTaskStatus({type: AsyncTaskStatusType.Success}));
                dispatch(setLogs(resultValue));
                return fulfillWithValue(resultValue);
            } else {
                dispatch(
                    setGetLogsTaskStatus({
                        type: AsyncTaskStatusType.Error,
                        errorMessage: message,
                        errorDetails,
                    })
                );
                return rejectWithValue(message);
            }
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || ErrorMessages.UNKNOWN_ERROR;
            dispatch(
                setGetLogsTaskStatus({
                    type: AsyncTaskStatusType.Error,
                    errorMessage,
                })
            );
            return rejectWithValue(errorMessage);
        }
    }
);

export const getMailLogsAction = createAsyncThunk(
    'logs/getMailLogs',
    async (
        {
            fromDate,
            toDate,
            page = 1,
        }: {
            fromDate: string | null;
            toDate: string | null;
            page: number;
        },
        {dispatch, getState, rejectWithValue, fulfillWithValue}
    ) => {
        dispatch(setGetMailLogsTaskStatus({type: AsyncTaskStatusType.Loading}));

        try {
            const token = await getAppToken(dispatch, getState as () => AppState);
            if (!token) {
                throw new Error(ErrorMessages.TOKEN_MISSING);
            }

            const params = new URLSearchParams({page: page.toString()});
            if (fromDate) params.append('fromDate', fromDate);
            if (toDate) params.append('toDate', toDate);

            const response = await axios.get(`${EndPoints.logs}/user/mail-logs?${params.toString()}`, {
                headers: {
                    [Headers.AUTHORIZATION]: `Bearer ${token}`,
                    [Headers.CONTENT_TYPE]: Headers.APPLICATION_JSON,
                    [Headers.ACCEPT]: Headers.APPLICATION_JSON,
                },
            });

            const {code, message, errorDetails, resultValue} = response.data;
            if (code === ResponseDTO.CodeEnum.Success) {
                dispatch(setGetMailLogsTaskStatus({type: AsyncTaskStatusType.Success}));
                dispatch(setMailLogs(resultValue));
                return fulfillWithValue(resultValue);
            } else {
                dispatch(setGetMailLogsTaskStatus({
                    type: AsyncTaskStatusType.Error,
                    errorMessage: message,
                    errorDetails,
                }));
                return rejectWithValue(message);
            }
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || ErrorMessages.UNKNOWN_ERROR;
            dispatch(setGetMailLogsTaskStatus({
                type: AsyncTaskStatusType.Error,
                errorMessage,
            }));
            return rejectWithValue(errorMessage);
        }
    }
);
